import { useTranslation } from "react-i18next";

import { useContextSelector } from "use-context-selector";
import { useTheme } from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { ColumnDef } from "@tanstack/react-table";
import { MdDoDisturb } from "react-icons/md";
import ReactLoading from "react-loading";

import { ProductList, OrderDetails } from "../../../../../../@types/Orders";
import { OrderItemProcessStatus } from "../../../../../../enums/Orders";

import { OrdersAccordion } from "../OrdersAccordion";
import { Table } from "../../../../../../components/New/Table";
import { ConfirmationModal } from "../../../../../../components/ConfirmationModal";

import { OrdersContext } from "../../../../../../contexts/OrdersContext";
import { useTranslateStatus } from "../../hooks/useTranslateStatus";
import { useAuth } from "../../../../../../hooks/auth";

import { colors } from "../../../../../../styles/themes/default/foundations/colors";
import MoneyBack from "../../../../../../assets/icons/money-back.svg";
import * as S from "./styles";
import {
  ActionButton,
  ActionsContainer,
  ActivityTextArea,
  AlignDataContainer,
  BillingText,
  DropshipQtyText,
  GridContainer,
  NotesContainer,
  NotesContainerAlign,
  NumberOfProducts,
  ProductListsTitle,
  ShipmentsContainer,
  StatusContainer,
  SumaryContentColumn,
  SumaryData,
  SumaryDataTitle,
} from "../OrderDetails.shared.styles";

import { formatDateWithTenantFormat } from "../../../../../../shared/Formatter";
import ErrorMessage from "../ErrorMessage";
import { formatQuantityWithRefund } from "../formatQuantityWithRefund";

const createErrorShipmentsColumns = (
  t: (key: string) => string,
  orderDetails: OrderDetails,
  translateItemProcessStatus: (status: OrderItemProcessStatus) => string,
  cancelProductListLineItem: (
    orderId: string,
    itemId: string,
  ) => void,
  loading: { cancelItem: boolean },
  canRefundAndCancelByCountry: boolean,
): ColumnDef<ProductList>[] => [
  {
    accessorFn: (row) => row.product?.sku || "",
    header: t("sku"),
    size: 130,
    cell: (props) => <BillingText>{props.getValue()}</BillingText>,
  },
  {
    accessorKey: "title",
    header: t("title"),
    size: 332,
    cell: (props) => <BillingText>{props.getValue()}</BillingText>,
  },
  {
    accessorFn: (row) => {
      const specializedStock = row.stockChecks?.find(
        (stock) => stock.source === "SPECIALIZED",
      );

      const color = () => {
        if (!specializedStock?.availableQuantity) {
          return colors["gray-850"];
        }

        if ((specializedStock?.availableQuantity ?? 0) < (row?.quantity ?? 0)) {
          return colors["red-450"];
        }
        return colors["gray-850"];
      };
      return {
        availableQuantity: specializedStock?.availableQuantity ?? "-",
        color: color(),
      };
    },
    header: t("sbcDropshipQty"),
    size: 150,
    cell: (props) => {
      const { availableQuantity, color } = props.getValue() as {
        availableQuantity: string;
        color: string;
      };

      return (
        <DropshipQtyText color={color}>{availableQuantity}</DropshipQtyText>
      );
    },
  },
  {
    accessorFn: (row) => {
      const lightspeedStock = row.stockChecks?.find(
        (stock) => stock.source === "LIGHTSPEED",
      );

      const color = () => {
        if (!lightspeedStock?.availableQuantity) {
          return colors["gray-850"];
        }

        if ((lightspeedStock?.availableQuantity ?? 0) < (row?.quantity ?? 0)) {
          return colors["red-450"];
        }
        return colors["gray-850"];
      };
      return {
        availableQuantity: lightspeedStock?.availableQuantity ?? "-",
        color: color(),
      };
    },
    header: t("lightspeedQty"),
    size: 150,
    cell: (props) => {
      const { availableQuantity, color } = props.getValue() as {
        availableQuantity: string;
        color: string;
      };

      return (
        <DropshipQtyText color={color}>{availableQuantity}</DropshipQtyText>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: t("quantity"),
    size: 100,
    cell: (props) => (
      <BillingText>
        {formatQuantityWithRefund(props.row.original, {
          fullyRefunded: t("fullyRefunded"),
          refunded: t("refundedQuantityLabel"),
        })}
      </BillingText>
    ),
  },
  {
    accessorKey: "processStatus",
    header: t("status"),
    size: 150,
    cell: (props) => (
      <StatusContainer variant={props.row.original.processStatus}>
        {translateItemProcessStatus(props.row.original.processStatus)}
      </StatusContainer>
    ),
  },
  {
    accessorKey: "",
    header: t("actions"),
    size: 70,
    cell: (props) => (
      <ActionsContainer>
        <ConfirmationModal
          title={t("confirmCancelOrderLine")}
          description={t("cancelOrderLineDescription")}
          variant="delete"
          confirmFunction={() =>
            cancelProductListLineItem(orderDetails.id, props.row.original.id)
          }
        >
          <ActionButton
            title={t("cancelItem")}
            disabled={
              props.row.original.processStatus === "CANCELLED" ||
              loading.cancelItem ||
              orderDetails.sourceMarketplace !== "SBC" ||
              !canRefundAndCancelByCountry
            }
          >
            {loading.cancelItem ? (
              <ReactLoading
                height={20}
                width={20}
                type={"spin"}
                color={colors["gray-800"]}
              />
            ) : (
              <MdDoDisturb size={20} />
            )}
          </ActionButton>
        </ConfirmationModal>
        <ActionButton title={t("refundItem")} disabled>
          <img
            src={MoneyBack}
            alt=""
            style={{ opacity: 0.5, width: 23, height: 23 }}
          />
        </ActionButton>
      </ActionsContainer>
    ),
  },
];

export function OrderDetailsError() {
  const { t } = useTranslation();

  const { translateItemProcessStatus } = useTranslateStatus();

  const { loading, orderDetails, cancelProductListLineItem } =
    useContextSelector(OrdersContext, (context) => {
      return context;
    });

  const {
    createdAt,
    requestedShippingCarrier,
    requestedShippingClass,
    privateNotes,
    publicNotes,
  } = orderDetails;

  const { responsiveDesign } = useTheme();

  const isMobile = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);

  const { canRefundAndCancelByCountry, shopSelected } = useAuth();

  const dateFormat = shopSelected?.tenant?.dateFormat;

  const shipmentsColumns = createErrorShipmentsColumns(
    t,
    orderDetails,
    translateItemProcessStatus,
    cancelProductListLineItem,
    loading,
    canRefundAndCancelByCountry,
  );

  const renderSummarySection = () => (
    <OrdersAccordion title={t("summary")}>
      <GridContainer>
        <SumaryContentColumn>
          <AlignDataContainer>
            <SumaryDataTitle>{t("lineItems")}:</SumaryDataTitle>
            <SumaryData>{orderDetails?.items?.length}</SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("buyer")}:</SumaryDataTitle>
            <SumaryData>
              {orderDetails?.billing?.lastName ??
                orderDetails?.shipping?.lastName}
              ,{" "}
              {orderDetails?.billing?.firstName ??
                orderDetails?.shipping?.firstName}
            </SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("created")}:</SumaryDataTitle>
            <SumaryData>
              {formatDateWithTenantFormat(createdAt, dateFormat, true)}
            </SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("imported")}:</SumaryDataTitle>
            <SumaryData>
              {formatDateWithTenantFormat(
                orderDetails?.importDateUTC,
                dateFormat,
                true,
              )}
            </SumaryData>
          </AlignDataContainer>
        </SumaryContentColumn>
        <SumaryContentColumn>
          <AlignDataContainer>
            <SumaryDataTitle>{t("channelAdvisorOrderId")}:</SumaryDataTitle>
            <SumaryData>{orderDetails?.orderReferenceNumber}</SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("sellerOrderId")}:</SumaryDataTitle>
            <SumaryData>{orderDetails?.secondaryStoreOrderID}</SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("siteOrderId")}:</SumaryDataTitle>
            <SumaryData>{orderDetails?.storeOrderID}</SumaryData>
          </AlignDataContainer>
        </SumaryContentColumn>
        <SumaryContentColumn>
          <AlignDataContainer>
            <SumaryDataTitle>{t("reqShippingShopify")}:</SumaryDataTitle>
            <SumaryData>{requestedShippingCarrier}</SumaryData>
          </AlignDataContainer>

          <AlignDataContainer>
            <SumaryDataTitle>{t("carrierClass")}:</SumaryDataTitle>
            <SumaryData>{requestedShippingClass}</SumaryData>
          </AlignDataContainer>
        </SumaryContentColumn>
      </GridContainer>
    </OrdersAccordion>
  );

  const renderProductListSection = () => (
    <OrdersAccordion title={t("productList")}>
      <ShipmentsContainer>
        <NumberOfProducts>
          {orderDetails.items.length} {t("productsListed")}
        </NumberOfProducts>

        {orderDetails?.items?.length && (
          <Table
            columns={shipmentsColumns}
            columnToFilter="task"
            data={orderDetails?.items}
            key={orderDetails.processStatus}
            style={{
              marginTop: isMobile ? "1rem" : "1.5rem",
            }}
          />
        )}
      </ShipmentsContainer>
    </OrdersAccordion>
  );

  const renderNotesSection = () => (
    <OrdersAccordion title={t("historyAndActivity")}>
      <ProductListsTitle
        style={{
          marginTop: isMobile ? "1.5rem" : "2rem",
          marginBottom: "1.5rem",
        }}
      >
        {t("notes")}
      </ProductListsTitle>

      <NotesContainerAlign>
        <NotesContainer>
          <SumaryData style={{ marginBottom: 8 }}>
            {t("publicNotes")}
          </SumaryData>
          <ActivityTextArea
            style={{ maxWidth: "31rem", maxHeight: "6rem" }}
            defaultValue={publicNotes}
            disabled
          />
        </NotesContainer>

        <NotesContainer>
          <SumaryData style={{ marginBottom: 8 }}>
            {t("privateNotes")}
          </SumaryData>
          <ActivityTextArea
            style={{ maxWidth: "31rem", maxHeight: "6rem" }}
            defaultValue={privateNotes}
            disabled
          />
        </NotesContainer>
      </NotesContainerAlign>
    </OrdersAccordion>
  );

  return (
    <>
      <ErrorMessage orderDetails={orderDetails} />

      {renderSummarySection()}

      <S.Separator />

      {renderProductListSection()}

      <S.Separator />

      {renderNotesSection()}
    </>
  );
}
